'use client'

import React, { useState, useEffect } from 'react'
import styles from './TerminalTabs.module.css'
import Holdings from './Holdings'
import OpenOrders from './OpenOrders'
import OrderHistory from './OrderHistory'
import GTTOrderHistory from './GTTOrderHistory'
import Positions from './Positions'
import { getSecureItem } from '../utils/secureStorage'

/**
 * @uiComponent
 * @name TerminalTabs
 * @description The main tabbed interface for the Groww trading terminal. Provides navigation between
 * different trading-related views including Open Orders, Positions, Order History, and Holdings.
 * Manages authentication credentials and passes them to child components.
 * @location Bottom panel of the trading interface
 * @prop none - No external props, manages internal state and retrieves credentials from session storage
 */

/**
 * @uiWorkflow
 * @name Navigate Trading Information on Groww Terminal
 * @description How to access different types of trading information and account data
 * @platform groww
 * @step 1 Locate the terminal tabs at the bottom of the trading interface [component: TerminalTabs] [action: observe]
 * @step 2 Click on "Open Orders" to view pending buy/sell orders [component: TerminalTabs] [action: click]
 * @step 3 Switch to "Positions" to see active trading positions [component: TerminalTabs] [action: click]
 * @step 4 Check "Order History" to review past completed orders [component: TerminalTabs] [action: click]
 * @step 5 View "Holdings" to see your long-term stock portfolio [component: TerminalTabs] [action: click]
 * @step 6 Use the tab navigation to switch between different views [component: TerminalTabs] [action: click]
 * @step 7 Each tab loads relevant data specific to that trading aspect [component: TerminalTabs] [action: observe]
 */

/**
 * @uiWorkflow
 * @name Monitor Trading Activity Overview on Groww
 * @description How to get a comprehensive view of your trading activity across all areas
 * @platform groww
 * @step 1 Start with the "Open Orders" tab to see pending activity [component: OpenOrders] [action: click]
 * @step 2 Review any orders waiting to be executed or filled [component: OpenOrders] [action: observe]
 * @step 3 Switch to "Positions" to check actively traded stocks [component: Positions] [action: click]
 * @step 4 Monitor real-time P&L on your current positions [component: Positions] [action: observe]
 * @step 5 Check "Order History" for recently completed transactions [component: OrderHistory] [action: click]
 * @step 6 Finally, review "Holdings" for your investment portfolio [component: Holdings] [action: click]
 * @step 7 Use this comprehensive view to make informed trading decisions [component: TerminalTabs] [action: analyze]
 */

interface OpenOrder {
  time: string
  side: 'BUY' | 'SELL'
  limitPrice: number
  size: number
  symbol: string
  stopLoss: number
  takeProfit: number
}

const TABS = ['Open Orders', 'Positions', 'Order History', 'Holdings']
// const TABS = ['Open Orders', 'Order History', 'GTT Orders', 'Trade History', 'Holdings']

export default function TerminalTabs() {
  const [activeTab, setActiveTab] = useState<string>(TABS[0])
  const [openOrders, setOpenOrders] = useState<OpenOrder[]>([])
  const [isLoading, setIsLoading] = useState(false)

  // Fetch open orders when component mounts
  useEffect(() => {
    const fetchOpenOrders = async () => {
      setIsLoading(true)
      try {
        const response = await fetch('/api/openorders')
        if (!response.ok) throw new Error('Failed to fetch orders')
        const data = await response.json()
        setOpenOrders(data)
      } catch (error) {
        console.error('Error fetching open orders:', error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchOpenOrders()
  }, [])

  const renderOpenOrders = () => {
    if (isLoading) return <div>Loading orders...</div>
    if (openOrders.length === 0) return <div>No open orders</div>

    return (
      <div className={styles.ordersTable}>
        <table>
          <thead>
            <tr>
              <th>Time</th>
              <th>Symbol</th>
              <th>Side</th>
              <th>Size</th>
              <th>Limit Price</th>
              <th>Stop Loss</th>
              <th>Take Profit</th>
            </tr>
          </thead>
          <tbody>
            {openOrders.map((order, index) => (
              <tr key={index}>
                <td>{order.time}</td>
                <td>{order.symbol.toUpperCase()}</td>
                <td
                  className={
                    order.side === 'BUY' ? styles.buyOrder : styles.sellOrder
                  }
                >
                  {order.side}
                </td>
                <td>{order.size}</td>
                <td>₹{order.limitPrice}</td>
                <td>₹{order.stopLoss}</td>
                <td>₹{order.takeProfit}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  const renderTabContent = () => {
    // Get credentials from session storage
    const apiKey = typeof window !== 'undefined' ? getSecureItem('groww_api_key') || '' : ''
    // Groww only requires API key for authentication
    const accessToken = ''
    
    switch (activeTab) {
      case 'Open Orders':
        return <OpenOrders apiKey={apiKey} accessToken={accessToken} />
      case 'Positions':
        return <Positions apiKey={apiKey} accessToken={accessToken} />
      case 'Order History':
        return <OrderHistory apiKey={apiKey} accessToken={accessToken} />
      case 'GTT Orders':
        return <GTTOrderHistory apiKey={apiKey} accessToken={accessToken} />
      case 'Trade History':
        return <div>Trade history data goes here...</div>
      case 'Holdings':
        return <Holdings apiKey={apiKey} accessToken={accessToken} />
      default:
        return null
    }
  }

  return (
    <div className={styles.tabsContainer}>
      <ul className={styles.tabList}>
        {TABS.map(tab => (
          <li
            key={tab}
            className={`${styles.tabItem} ${
              tab === activeTab ? styles.activeTab : ''
            }`}
            onClick={() => setActiveTab(tab)}
          >
            {tab}
          </li>
        ))}
      </ul>
      <div className={styles.tabContent}>{renderTabContent()}</div>
    </div>
  )
}
